/**
 * Sandpiper Management - API Service Factory & Constants
 *
 * Updated to support OAuth tokens from Cognito.
 * The access token is fetched dynamically via a getter function so that
 * refreshed tokens are picked up automatically without recreating the service.
 */

// ============================================
// Constants
// ============================================

export const DEFAULT_SERVER_URL = import.meta.env.VITE_API_SERVER_URL || 'http://localhost';

export const DEVICE_STATUS_COLORS: Record<string, string> = {
  online: 'bg-green-500',
  sleeping: 'bg-yellow-500',
  offline: 'bg-gray-500',
  unknown: 'bg-red-500',
};

// ============================================
// Types
// ============================================

export interface BlobResponse {
  blob: Blob;
  lastModified: string | null;
}

// ============================================
// API Service Factory
// ============================================

/**
 * Create an API service instance.
 *
 * @param baseUrl - Sandpiper API base URL
 * @param tokenOrGetter - Either a static Bearer token string (legacy API key mode)
 *   or a function that returns the current access token (OAuth mode).
 */
export const createApiService = (baseUrl: string, tokenOrGetter: string | (() => string | null)) => {
  const normalizedBase = baseUrl.replace(/\/+$/, '');

  // Resolve the current authorization header dynamically
  const getAuthHeaders = (): Record<string, string> => {
    const token = typeof tokenOrGetter === 'function' ? tokenOrGetter() : tokenOrGetter;
    if (!token) return {};
    return { Authorization: `Bearer ${token}` };
  };

  const buildUrl = (endpoint: string, params: Record<string, string | number | undefined | null> = {}): string => {
    const normalizedEndpoint = endpoint.replace(/^\/+/, '');
    const url = new URL(`${normalizedBase}/${normalizedEndpoint}`);
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        url.searchParams.set(key, String(value));
      }
    });
    return url.toString();
  };

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const fetchJson = async (endpoint: string, params: Record<string, any> = {}): Promise<any> => {
    const res = await fetch(buildUrl(endpoint, params), { headers: getAuthHeaders() });
    if (res.status === 401) throw new Error('Unauthorized — session may have expired');
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    return res.json();
  };

  const fetchBlob = async (endpoint: string): Promise<BlobResponse> => {
    const res = await fetch(buildUrl(endpoint), { headers: getAuthHeaders() });
    if (res.status === 401) throw new Error('Unauthorized — session may have expired');
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    const lastModified = res.headers.get('last-modified');
    const blob = await res.blob();
    return { blob, lastModified };
  };

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const putJson = async (endpoint: string, data: Record<string, any> = {}): Promise<any> => {
    const res = await fetch(buildUrl(endpoint), {
      method: 'PUT',
      headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    });
    if (res.status === 401) throw new Error('Unauthorized — session may have expired');
    return res.json();
  };

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const put = async (endpoint: string): Promise<any> => {
    const res = await fetch(buildUrl(endpoint), {
      method: 'PUT',
      headers: getAuthHeaders(),
    });
    if (res.status === 401) throw new Error('Unauthorized — session may have expired');
    return res.json();
  };

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const postJson = async (endpoint: string, data: any): Promise<any> => {
    const res = await fetch(buildUrl(endpoint), {
      method: 'POST',
      headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    });
    if (res.status === 401) throw new Error('Unauthorized — session may have expired');
    return res.json();
  };

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const deleteReq = async (endpoint: string): Promise<any> => {
    const res = await fetch(buildUrl(endpoint), {
      method: 'DELETE',
      headers: getAuthHeaders(),
    });
    if (res.status === 401) throw new Error('Unauthorized — session may have expired');
    return res.json();
  };

  return {
    fetchJson,
    fetchBlob,
    putJson,
    put,
    postJson,
    deleteReq,

    // Firmware
    getFirmwareList: () => fetchJson('firmware/list'),

    // Devices
    getAllDevices: (cursor?: string, size = 250) => fetchJson('device/all', { cursor, size }),
    getDevice: (id: string) => fetchJson(`device/${id}`),
    updateDevice: (id: string, { url, rotation, firmware }: { url?: string; rotation?: string | number; firmware?: string }) => putJson(`device/${id}`, { url, rotation, firmware }),
    registerDevice: (id: string, { url, rotation }: { url?: string; rotation?: string | number }) => postJson(`device/${id}`, { url, rotation }),
    deleteDevice: (id: string) => deleteReq(`device/${id}`),

    // Device Logs
    getDeviceLogMeta: (id: string) => fetchJson(`device/${id}/log`),
    getDeviceLog: (id: string, index: number) => fetchJson(`device/${id}/log/${index}`) as Promise<{ url: string }>,

    // Device Screenshot
    getDeviceScreenshot: (id: string) => fetchBlob(`device/${id}/screenshot`),

    // Device Control
    sendCommand: (id: string, command: string) => putJson(`device/${id}`, { command }),
    requestScreenshot: (id: string) => putJson(`device/${id}`, { command: 'send screenshot' }),
    requestLogs: (id: string) => put(`device/${id}/log_request`),
    requestInfo: (id: string) => putJson(`device/${id}`, { command: 'send info' }),
    restartDevice: (id: string) => put(`device/${id}/restart`),
  };
};

export type ApiService = ReturnType<typeof createApiService>;
